import { useQuery } from '@tanstack/react-query';
import { env } from './env';
import { refreshToken } from '@/api/client';
import { getStoredAppLanguage, setAppLanguage } from '@/locales/i18n';
import { FEATURES } from '@/domain/authentication';
import type {
  User,
  AuthState,
  Feature,
  UserLanguage,
} from '@/domain/authentication';

export interface AppUser {
  id: string;
  email: string;
  name?: string;
  language?: UserLanguage;
  company?: {
    name: string;
    uuid: string;
  }[];
  features: string[];
  activeJoinRequest?: {
    id: string;
    companyName: string;
  };
}

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

export function hasAuthCookie(): boolean {
  return getCookie('auth_token') !== null;
}

export async function verifyAuth(): Promise<User | null> {
  try {
    let authResponse = await getAuthUser();
    if (authResponse.error) {
      return null;
    } else if (authResponse.notAuthenticated) {
      try {
        await refreshToken();
        authResponse = await getAuthUser();
        // If refresh still results in not authenticated, return null
        if (authResponse.notAuthenticated || authResponse.error) {
          return null;
        }
      } catch (refreshError) {
        console.log('TOKEN REFRESH ERROR', refreshError);
        return null;
      }
    }

    return authResponse.user;
  } catch (e) {
    console.log('AUTH ERROR', e);
    return null;
  }
}

async function getAuthUser(): Promise<{
  user: User | null;
  notAuthenticated: boolean;
  error: boolean;
}> {
  const response = await fetch(`${env.apiBaseUrl}/v1/me`, {
    method: 'GET',
    credentials: 'include',
  });

  if (response.ok) {
    const appUser: AppUser = await response.json();
    const features = appUser.features || [];
    const storedLanguage = getStoredAppLanguage();

    if (
      appUser.language?.isoCode &&
      storedLanguage !== appUser.language.isoCode
    ) {
      await setAppLanguage(appUser.language.isoCode);
    }

    // Validate feature types
    const validFeatures: Feature[] = features.filter(
      (feature): feature is Feature =>
        (FEATURES as readonly string[]).includes(feature)
    );

    const user = {
      id: appUser.id,
      email: appUser.email,
      name: appUser.name,
      language: appUser.language,
      companyName: appUser.company?.[0]?.name,
      companyUuid: appUser.company?.[0]?.uuid,
      isCompanyAssigned:
        appUser.company !== undefined && appUser.company.length > 0,
      features: validFeatures,
      canUseWebapp: validFeatures.includes('webapp'),
      activeJoinRequest: appUser.activeJoinRequest,
    };

    return {
      user,
      notAuthenticated: false,
      error: false,
    };
  } else if (response.status === 401) {
    return {
      user: null,
      notAuthenticated: true,
      error: false,
    };
  }

  return {
    user: null,
    notAuthenticated: false,
    error: true,
  };
}

export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

export function useAuth(): AuthState {
  const { data: user, isLoading } = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: verifyAuth,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    isAuthenticated: user !== null && user !== undefined,
    isLoading,
    user: user ?? null,
  };
}
